import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { LOGO, NAV_LINKS, PHONE, PHONE_HREF } from "@/lib/site-data";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-[70] transition-[background-color,padding,border-color] duration-300 ${
          scrolled
            ? "bg-black/85 backdrop-blur-xl border-b border-white/10 py-2"
            : "bg-gradient-to-b from-black/80 to-transparent py-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between gap-4">
          <a href="#home" data-testid="nav-logo-link" className="shrink-0">
            <img
              src={LOGO}
              alt="Phillips Waterproofing & Concrete"
              className={`w-auto transition-[height] duration-300 ${scrolled ? "h-10" : "h-12 md:h-14"}`}
            />
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="link-underline text-[13px] font-semibold tracking-wide text-gray-300 hover:text-white transition-colors duration-200"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={PHONE_HREF}
              data-testid="nav-call-now-btn"
              className="hidden md:flex items-center gap-2 text-sm font-bold text-white hover:text-cyan-400 transition-colors duration-200"
            >
              <Phone size={16} className="text-cyan-400" />
              <span className="hidden xl:inline">CALL NOW&nbsp;</span>{PHONE}
            </a>
            <a
              href="#estimate"
              data-testid="nav-free-estimate-btn"
              className="hidden sm:inline-flex items-center rounded-md bg-[#0066FF] px-5 py-2.5 text-sm font-bold tracking-wide text-white transition-[background-color,box-shadow] duration-300 hover:bg-[#00E5FF] hover:text-black hover:shadow-[0_0_24px_rgba(0,229,255,0.5)]"
            >
              FREE ESTIMATE
            </a>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(true)}
              className="lg:hidden text-white p-2"
              aria-label="Open menu"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[90] bg-black/95 backdrop-blur-xl flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-4">
              <img src={LOGO} alt="Phillips Waterproofing & Concrete" className="h-11 w-auto" />
              <button
                data-testid="mobile-menu-close-btn"
                onClick={() => setOpen(false)}
                className="text-white p-2"
                aria-label="Close menu"
              >
                <X size={28} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.label}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  className="font-display text-3xl font-semibold text-white/90 py-2 hover:text-cyan-400 transition-colors duration-200"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <div className="px-8 pb-10 flex flex-col gap-3">
              <a
                href={PHONE_HREF}
                data-testid="mobile-menu-call-btn"
                className="flex items-center justify-center gap-2 rounded-md border border-cyan-400/50 py-3.5 font-bold text-cyan-400"
              >
                <Phone size={18} /> CALL NOW — {PHONE}
              </a>
              <a
                href="#estimate"
                data-testid="mobile-menu-estimate-btn"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center rounded-md bg-[#0066FF] py-3.5 font-bold text-white"
              >
                FREE ESTIMATE
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
